/**
 * Reguły topologii obiegu równoległego (rozdzielenie `parallel_split` → gałęzie → scalenie
 * `parallel_join`) — część reguł publikacji, wołana z `validateStageRoutes`
 * (`process-route-rules.ts`), więc ten sam kod sprawdza proces tenanta, szablon ITE i kreator.
 *
 * Po co: silnik umie dziś tylko jeden kształt obiegu równoległego. Za scaleniem obsługuje
 * wyłącznie etap końcowy, na początku gałęzi wyłącznie etap akceptacji, a scalenie liczy
 * gałęzie jednego rozdzielenia — inny kształt kończy się błędem serwera DOPIERO przy starcie
 * albo w trakcie sprawy. Te reguły zatrzymują go przy publikacji.
 *
 * Reguły (wszystkie blokujące; każda raportuje id węzłów, które ją łamią):
 *   - `PARALLEL_JOIN_EXIT_NOT_END` — scalenie ma dokładnie jedną drogę wyjściową i prowadzi
 *     ona do etapu końcowego (raportuje scalenie),
 *   - `PARALLEL_SPLIT_EXIT_NOT_STAGE` — każda droga z rozdzielenia prowadzi do etapu akceptacji
 *     i do INNEGO niż pozostałe (dwie drogi do tego samego etapu to jedna gałąź policzona
 *     dwa razy; raportuje rozdzielenie),
 *   - `PARALLEL_SPLIT_NESTED` — rozdzielenie nie leży wewnątrz gałęzi innego rozdzielenia
 *     (raportuje wewnętrzne rozdzielenie),
 *   - `PARALLEL_SPLIT_JOIN_MISMATCH` — rozdzielenie ma co najmniej dwie gałęzie, KAŻDA gałąź ma
 *     drogę do scalenia (dodatkowo może mieć drogi do Końca), gałęzie razem docierają do
 *     dokładnie jednego scalenia, a scalenie dostaje gałęzie dokładnie jednego rozdzielenia
 *     (raportuje rozdzielenie i/lub scalenie),
 *   - `PARALLEL_BRANCH_BOUNDARY` — drogi nie przekraczają granicy gałęzi (raportuje źródła
 *     krawędzi łamiących granicę oraz etapy wspólne dla dwóch gałęzi).
 *
 * Model (czysta topologia, bez bazy i bez silnika — silnik ma własne liczenie gałęzi):
 *   - „początek” = etapy akceptacji osiągalne ze `start` bez przechodzenia przez rozdzielenia,
 *     scalenia i końce, oraz bez wchodzenia w pierwsze etapy gałęzi (te należą do gałęzi);
 *   - gałąź = droga wychodząca z rozdzielenia; jej pierwszy etap to cel tej drogi, a jej etapy to
 *     etapy akceptacji osiągalne z pierwszego etapu bez wchodzenia w początek ani w pierwszy etap
 *     INNEJ gałęzi. Węzły graniczne (scalenie, rozdzielenie, koniec) zatrzymują przejście —
 *     scalenia, do których gałąź dociera, określają jej scalenie.
 *   - Krawędzie bierzemy wszystkie (tak jak silnik przy liczeniu gałęzi), niezależnie od typu.
 *
 * Graf bez rozdzieleń i scaleń nie łamie żadnej z tych reguł (wczesne wyjście).
 */

/** Kody błędów publikacji wynikające z topologii obiegu równoległego. */
export const PARALLEL_RULE_ISSUES = [
  'PARALLEL_JOIN_EXIT_NOT_END',
  'PARALLEL_SPLIT_EXIT_NOT_STAGE',
  'PARALLEL_SPLIT_NESTED',
  'PARALLEL_SPLIT_JOIN_MISMATCH',
  'PARALLEL_BRANCH_BOUNDARY',
] as const;

export type ParallelRuleIssue = (typeof PARALLEL_RULE_ISSUES)[number];

/** Per kod błędu: id węzłów łamiących regułę (puste = reguła spełniona). */
export type ParallelRuleViolations = Record<ParallelRuleIssue, string[]>;

export interface ParallelRuleNode {
  id: string;
  nodeType: string;
}

export interface ParallelRuleEdge {
  sourceId: string;
  targetId: string;
}

/** Jedna gałąź rozdzielenia: jej etapy i scalenia, do których któryś z etapów ma drogę. */
interface Branch {
  splitId: string;
  stages: Set<string>;
  joins: Set<string>;
}

/**
 * Obiekt z wartością dla KAŻDEGO kodu z `PARALLEL_RULE_ISSUES` — kody bierzemy z jednej listy,
 * więc dołożenie reguły nie wymaga poprawiania inicjalizacji. Rzutowanie jest konieczne, bo
 * `Object.fromEntries` zwraca zawsze słownik po `string`; kompletność kluczy zapewnia pętla
 * po całej tablicy `as const`.
 */
function mapIssueCodes<T>(build: (code: ParallelRuleIssue) => T): Record<ParallelRuleIssue, T> {
  return Object.fromEntries(PARALLEL_RULE_ISSUES.map((code) => [code, build(code)])) as Record<
    ParallelRuleIssue,
    T
  >;
}

export function validateParallelTopology(
  nodes: readonly ParallelRuleNode[],
  edges: readonly ParallelRuleEdge[],
): ParallelRuleViolations {
  const flagged = mapIssueCodes<Set<string>>(() => new Set());
  const result = (): ParallelRuleViolations => mapIssueCodes((code) => [...flagged[code]]);

  const typeById = new Map<string, string>(nodes.map((n) => [n.id, n.nodeType]));
  const splitIds = nodes.filter((n) => n.nodeType === 'parallel_split').map((n) => n.id);
  const joinIds = nodes.filter((n) => n.nodeType === 'parallel_join').map((n) => n.id);
  if (splitIds.length === 0 && joinIds.length === 0) return result();

  const targetsBySource = new Map<string, string[]>();
  for (const edge of edges) {
    const list = targetsBySource.get(edge.sourceId) ?? [];
    list.push(edge.targetId);
    targetsBySource.set(edge.sourceId, list);
  }
  const targetsOf = (id: string): readonly string[] => targetsBySource.get(id) ?? [];
  const isDecision = (id: string): boolean => typeById.get(id) === 'decision';

  // 1. Za scaleniem wolno tylko jeden etap końcowy: dokładnie jedna krawędź i do `end`.
  for (const joinId of joinIds) {
    const exits = targetsOf(joinId);
    const onlyExit = exits[0];
    if (exits.length !== 1 || onlyExit === undefined || typeById.get(onlyExit) !== 'end') {
      flagged.PARALLEL_JOIN_EXIT_NOT_END.add(joinId);
    }
  }

  // 2. Gałąź zaczyna się od etapu akceptacji — każda droga z rozdzielenia musi do takiego prowadzić
  //    i każda do INNEGO (dwie drogi do tego samego etapu to jedna gałąź, nie dwie).
  for (const splitId of splitIds) {
    const targets = targetsOf(splitId);
    const hasDuplicateTarget = new Set(targets).size !== targets.length;
    if (hasDuplicateTarget || targets.some((target) => !isDecision(target))) {
      flagged.PARALLEL_SPLIT_EXIT_NOT_STAGE.add(splitId);
    }
  }

  // 3. Pierwsze etapy gałęzi (różne cele dróg rozdzielenia będące etapami akceptacji).
  const rootsBySplit = new Map<string, string[]>();
  const allRoots = new Set<string>();
  for (const splitId of splitIds) {
    const roots = [...new Set(targetsOf(splitId).filter(isDecision))];
    rootsBySplit.set(splitId, roots);
    for (const root of roots) allRoots.add(root);
  }

  // 4. Początek: etapy ze `start`, bez przechodzenia przez rozdzielenia/scalenia/końce i bez
  //    wchodzenia w pierwsze etapy gałęzi (wejście do nich z początku łamie granicę — krok 7).
  const prefix = new Set<string>();
  const prefixQueue: string[] = nodes.filter((n) => n.nodeType === 'start').map((n) => n.id);
  while (prefixQueue.length > 0) {
    const current = prefixQueue.shift();
    if (current === undefined) break;
    for (const target of targetsOf(current)) {
      if (!isDecision(target) || prefix.has(target) || allRoots.has(target)) continue;
      prefix.add(target);
      prefixQueue.push(target);
    }
  }

  // 5. Gałęzie: zasięg każdej z pierwszego etapu. Przejście zatrzymują: początek i pierwsze
  //    etapy INNYCH gałęzi (to wyjście poza gałąź — krok 7, zbiór gałęzi się nie zanieczyszcza),
  //    a także scalenie, rozdzielenie i koniec (węzły graniczne — nie są etapami gałęzi).
  const branches: Branch[] = [];
  const branchesBySplit = new Map<string, Branch[]>();
  const joinsOfSplit = new Map<string, Set<string>>();
  for (const splitId of splitIds) {
    const splitJoins = new Set<string>();
    const splitBranches: Branch[] = [];
    branchesBySplit.set(splitId, splitBranches);
    // Droga prosto z rozdzielenia do scalenia też „dociera do scalenia" (wpada w krok 2).
    for (const target of targetsOf(splitId)) {
      if (typeById.get(target) === 'parallel_join') splitJoins.add(target);
    }
    for (const rootId of rootsBySplit.get(splitId) ?? []) {
      const branch: Branch = { splitId, stages: new Set([rootId]), joins: new Set() };
      const queue: string[] = [rootId];
      while (queue.length > 0) {
        const current = queue.shift();
        if (current === undefined) break;
        for (const target of targetsOf(current)) {
          const targetType = typeById.get(target);
          if (targetType === 'decision') {
            if (branch.stages.has(target) || prefix.has(target) || allRoots.has(target)) continue;
            branch.stages.add(target);
            queue.push(target);
          } else if (targetType === 'parallel_join') {
            branch.joins.add(target);
            splitJoins.add(target);
          }
        }
      }
      branches.push(branch);
      splitBranches.push(branch);
    }
    joinsOfSplit.set(splitId, splitJoins);
  }

  // 6. Etap wspólny dla dwóch gałęzi (zbiegają się przed scaleniem) łamie granicę gałęzi.
  const branchesByStage = new Map<string, Branch[]>();
  for (const branch of branches) {
    for (const stage of branch.stages) {
      const list = branchesByStage.get(stage) ?? [];
      list.push(branch);
      branchesByStage.set(stage, list);
    }
  }
  for (const [stage, owners] of branchesByStage) {
    if (owners.length > 1) flagged.PARALLEL_BRANCH_BOUNDARY.add(stage);
  }

  // 7. Krawędzie przekraczające granicę gałęzi.
  for (const { sourceId, targetId } of edges) {
    const sourceType = typeById.get(sourceId);
    const targetType = typeById.get(targetId);
    const sourceBranches = branchesByStage.get(sourceId) ?? [];
    const isStageOrStart = sourceType === 'decision' || sourceType === 'start';

    // (a) Z etapu gałęzi — tylko do etapu TEJ SAMEJ gałęzi (także wstecz), do scalenia albo do
    //     końca. Etap przed rozdzieleniem, inna gałąź, własne rozdzielenie, start i węzły
    //     nieznane łamią granicę; inne rozdzielenie to rozdzielenie zagnieżdżone.
    if (sourceType === 'decision' && sourceBranches.length > 0) {
      if (targetType === 'decision') {
        if (!sourceBranches.some((b) => b.stages.has(targetId))) {
          flagged.PARALLEL_BRANCH_BOUNDARY.add(sourceId);
        }
      } else if (targetType === 'parallel_split') {
        if (sourceBranches.some((b) => b.splitId === targetId)) {
          flagged.PARALLEL_BRANCH_BOUNDARY.add(sourceId);
        } else {
          flagged.PARALLEL_SPLIT_NESTED.add(targetId);
        }
      } else if (targetType !== 'parallel_join' && targetType !== 'end') {
        flagged.PARALLEL_BRANCH_BOUNDARY.add(sourceId);
      }
    }

    // (b) Do etapu gałęzi wchodzi się z etapu tej samej gałęzi. Wejścia z rozdzielenia nie
    //     podlegają tej regule, bo źródłem nie jest tu etap ani start (`isStageOrStart`);
    //     z rozdzielenia wchodzi się do pierwszych etapów gałęzi z definicji.
    if (isStageOrStart && targetType === 'decision') {
      const targetBranches = branchesByStage.get(targetId) ?? [];
      if (targetBranches.length > 0 && !targetBranches.some((b) => b.stages.has(sourceId))) {
        flagged.PARALLEL_BRANCH_BOUNDARY.add(sourceId);
      }
    }

    // (d) Do scalenia nie prowadzi droga spoza gałęzi (rozdzielenie → scalenie ma osobną regułę).
    if (isStageOrStart && targetType === 'parallel_join' && sourceBranches.length === 0) {
      flagged.PARALLEL_BRANCH_BOUNDARY.add(sourceId);
    }
  }

  // 8. Rozdzielenie ↔ scalenie: rozdzielenie ma co najmniej dwie gałęzie, każda z nich ma drogę
  //    do scalenia (do Końca może mieć dodatkowo), gałęzie razem docierają do dokładnie jednego
  //    scalenia (zero = rozdzielenie bez scalenia, ≥ 2 = rozjazd), a scalenie dostaje gałęzie
  //    dokładnie jednego rozdzielenia (zero = scalenie bez rozdzielenia, ≥ 2 = wspólne).
  //    Gałąź z drogą do scalenia innego niż pozostałe jest już rozjazdem (≥ 2 scalenia).
  const splitsOfJoin = new Map<string, Set<string>>();
  for (const splitId of splitIds) {
    const splitJoins = joinsOfSplit.get(splitId) ?? new Set<string>();
    const splitBranches = branchesBySplit.get(splitId) ?? [];
    if (
      splitJoins.size !== 1 ||
      splitBranches.length < 2 ||
      splitBranches.some((branch) => branch.joins.size === 0)
    ) {
      flagged.PARALLEL_SPLIT_JOIN_MISMATCH.add(splitId);
    }
    for (const joinId of splitJoins) {
      const set = splitsOfJoin.get(joinId) ?? new Set<string>();
      set.add(splitId);
      splitsOfJoin.set(joinId, set);
    }
  }
  for (const joinId of joinIds) {
    if ((splitsOfJoin.get(joinId)?.size ?? 0) !== 1) {
      flagged.PARALLEL_SPLIT_JOIN_MISMATCH.add(joinId);
    }
  }

  return result();
}
